import dayjs from 'dayjs';
import React, { useCallback, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { RhNotification } from '../../../api/models/RhNotification';
import { communicationSlice } from '../../../model/communicationSlice';
import { RootState } from '../../../model/store';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { CommunicationsScreen } from '../components/CommunicationsScreen';
import { UploadResult } from '../../../api/models/UploadResult';

export type CommunicationsProps = {
  // Outputs
  isFormModalOpen: boolean;
  scheduledNotifications: RhNotification[];
  pastNotifications: RhNotification[];
  isLoading: boolean;
  formState: NotificationFormState;
  isModalLoading: boolean;
  isFormFilled: boolean;
  indexBeingDeleted: number | null;

  // Inputs
  userClickedDeleteOnACard: (payload: { id: string; index: number; type: 'past' | 'schedule' }) => void;
  userToggledFormModal: () => void;
  userUpdatedFormState: React.Dispatch<React.SetStateAction<NotificationFormState>>;
  userCancelledFormModal: () => void;
  userClickedOKOnFormModal: () => void;
};

type NotificationFormState = {
  title: string;
  content: string;
  date: Date | null;
  time: Date | null;
  thumbnail: File | null;
};

const initialFormState: NotificationFormState = {
  title: '',
  content: '',
  date: new Date(),
  time: null,
  thumbnail: null,
};

const validateNotificationFormState = (formState: NotificationFormState) => {
  if (!formState.title) {
    return 'Title is required';
  } else if (!formState.content) {
    return 'Message is required';
  } else if (!formState.date || !formState.time) {
    return 'Date and time is required';
  }
  return '';
};

/**
 * Why is this not following the same structure as (link below)?
 * https://github.com/smashingboxes/arno/blob/dev/src/modules/home/containers/HomeContainer.tsx
 *
 * Reason 1: Don’t call Hooks inside loops, conditions, or nested functions
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level)
 * Reason 2: Don’t call Hooks from regular JavaScript functions.
 * (https://reactjs.org/docs/hooks-rules.html#only-call-hooks-from-react-functions)
 */
export const CommunicationsContainer = () => {
  const scheduledNotifications = useSelector((state: RootState) => state.communication.scheduledList);
  const pastNotifications = useSelector((state: RootState) => state.communication.pastList);

  const [isNewNotificationFormModalOpen, setIsNewNotificationFormModalOpen] = useState(false);
  const [formState, setFormState] = useState<NotificationFormState>(initialFormState);
  const [addingNotification, setAddingNotification] = useState(false);
  const [deletingNotification, setDeletingNotification] = useState(false);
  const [notificationIndexBeingDeleted, setNotificationIndexBeingDeleted] = useState<number | null>(null);

  const request = useRequest();
  const dispatch = useDispatch();

  const { loading: loadingScheduledNotifications } = useAsyncRequest(
    useCallback(() => request<RhNotification[]>(apiConfigs.getNotifications('schedule')), [request]),
    useCallback(
      scheduledNotifications => dispatch(communicationSlice.actions.receivedScheduledList({ scheduledNotifications })),
      [dispatch]
    )
  );

  const { loading: loadingPastNotifications } = useAsyncRequest(
    useCallback(() => request<RhNotification[]>(apiConfigs.getNotifications('past')), [request]),
    useCallback(pastNotifications => dispatch(communicationSlice.actions.receivedPastList({ pastNotifications })), [
      dispatch,
    ])
  );

  return (
    <CommunicationsScreen
      isLoading={loadingScheduledNotifications || loadingPastNotifications || deletingNotification}
      isModalLoading={addingNotification}
      isFormModalOpen={isNewNotificationFormModalOpen}
      isFormFilled={!validateNotificationFormState(formState)}
      scheduledNotifications={scheduledNotifications || []}
      pastNotifications={pastNotifications || []}
      formState={formState}
      indexBeingDeleted={notificationIndexBeingDeleted}
      userUpdatedFormState={setFormState}
      userToggledFormModal={() => setIsNewNotificationFormModalOpen(open => !open)}
      userClickedDeleteOnACard={({ id, index, type }) => {
        confirmAlert({
          customUI: options => (
            <RhConfirmModal
              isOpen={true}
              description="Are you sure you want to delete this notification?"
              onClose={options.onClose}
              onOk={async () => {
                options.onClose();
                try {
                  setDeletingNotification(true);
                  setNotificationIndexBeingDeleted(index);

                  await request(apiConfigs.deleteNotification(id));
                  const newNotifications = await request<RhNotification[]>(
                    apiConfigs.getNotifications(type === 'schedule' ? 'schedule' : 'past')
                  );

                  if (type === 'schedule') {
                    dispatch(
                      communicationSlice.actions.receivedScheduledList({
                        scheduledNotifications: newNotifications,
                      })
                    );
                  } else {
                    dispatch(
                      communicationSlice.actions.receivedPastList({
                        pastNotifications: newNotifications,
                      })
                    );
                  }

                  setNotificationIndexBeingDeleted(null);
                  setDeletingNotification(false);

                  toast.success('Notification deleted!');
                } catch (e: any) {
                  toast.error(e.message || 'Failed to delete notification');
                  setDeletingNotification(false);
                }
              }}
            />
          ),
        });
      }}
      userCancelledFormModal={() => {
        setIsNewNotificationFormModalOpen(open => !open);
        setFormState(initialFormState);
      }}
      userClickedOKOnFormModal={async () => {
        try {
          const validationError = validateNotificationFormState(formState);
          if (validationError) {
            throw new Error(validationError);
          }

          const dDate = dayjs(formState.date!);
          const dTime = dayjs(formState.time!);

          setAddingNotification(true);

          let imageUrl = '';
          if (formState.thumbnail) {
            const uploadResult = await request<UploadResult>(apiConfigs.uploadImage(formState.thumbnail));
            imageUrl = uploadResult.filePath;
          }

          await request(
            apiConfigs.addNotification({
              title: formState.title,
              content: formState.content,
              notifyDateTime: new Date(
                dDate.get('year'),
                dDate.get('month'),
                dDate.get('date'),
                dTime.get('hour'),
                dTime.get('minute')
              ),
              image: imageUrl,
            })
          );

          const newScheduledNotifications = await request<RhNotification[]>(apiConfigs.getNotifications('schedule'));
          const newPastNotifications = await request<RhNotification[]>(apiConfigs.getNotifications('past'));

          dispatch(
            communicationSlice.actions.receivedScheduledList({
              scheduledNotifications: newScheduledNotifications,
            })
          );
          dispatch(
            communicationSlice.actions.receivedPastList({
              pastNotifications: newPastNotifications,
            })
          );

          setAddingNotification(false);
          setIsNewNotificationFormModalOpen(false);
          setFormState(initialFormState);

          toast.success('Notification added!');
        } catch (e: any) {
          toast.error(e.message || 'Failed to create new notification');
          setAddingNotification(false);
        }
      }}
    />
  );
};
